import { useState, useEffect } from "react";
import { cancelOpenHouse } from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";
import {
  composeCancellationSellerEmail,
  composeCancellationAgentEmail,
} from "../../utils/openHouseEmailComposer";
import { sendOpenHouseEmail } from "../../utils/emailSender";
import { sendListingAgentCopy } from "../../utils/openHouseSend";

export default function CancelModal({ open, onClose, openHouse, onCancelled }) {
  const [{ user, emailTemplates }] = useStateValue();
  const [reason, setReason] = useState("");
  const [notifySeller, setNotifySeller] = useState(true);
  const [notifyAgent, setNotifyAgent] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setNotifySeller(true);
      setNotifyAgent(true);
    }
  }, [open, openHouse?.id]);

  if (!open || !openHouse) return null;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await cancelOpenHouse(openHouse.id, reason, user.id);
      toast.success("Open house cancelled.");

      // Fire cancellation emails for checked boxes (fire-and-forget).
      if (notifySeller && openHouse.sellerEmail) {
        const { subject, text, html } = composeCancellationSellerEmail(openHouse, reason, user, emailTemplates?.["cancellation-seller"]);
        sendOpenHouseEmail({
          userId: user.id,
          to: openHouse.sellerEmail,
          toName: openHouse.sellerName,
          subject,
          text,
          html,
          type: "cancellation",
          relatedDealId: openHouse.dealId,
          relatedOpenHouseId: openHouse.id,
        }).then((r) => {
          if (!r.ok) toast.warn(`Seller cancellation email failed: ${r.error}`);
        });
      }
      if (notifyAgent && openHouse.hostingAgentEmail) {
        const { subject, text, html } = composeCancellationAgentEmail(openHouse, reason, user, emailTemplates?.["cancellation-agent"]);
        sendOpenHouseEmail({
          userId: user.id,
          to: openHouse.hostingAgentEmail,
          toName: openHouse.hostingAgentName,
          subject,
          text,
          html,
          type: "cancellation",
          relatedDealId: openHouse.dealId,
          relatedOpenHouseId: openHouse.id,
        }).then((r) => {
          if (!r.ok) toast.warn(`Agent cancellation email failed: ${r.error}`);
        });
      }

      {
        const { subject, text, html } = composeCancellationAgentEmail(
          openHouse,
          reason,
          user,
          emailTemplates?.["cancellation-agent"],
        );
        sendListingAgentCopy({
          oh: openHouse,
          sender: user,
          subject,
          text,
          html,
          type: "cancellation",
          excludeEmails: [openHouse.sellerEmail, openHouse.hostingAgentEmail],
        }).catch((err) =>
          console.error("Listing-agent cancellation copy failed:", err),
        );
      }

      onCancelled && onCancelled(openHouse.id);
      onClose();
    } catch (err) {
      console.error("CancelModal error:", err);
      toast.error("Could not cancel. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/55 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Cancel Open House</h3>
          <p className="text-xs text-slate-500 mt-1">
            {openHouse.propertyAddress?.split(",")[0]} · {openHouse.date}
          </p>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm"
              placeholder="e.g. Weather forecast, scheduling conflict, seller request..."
            />
            <p className="text-xs text-slate-400 mt-1">
              Included in the cancellation emails so the recipients know what happened.
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2">Notify</p>
            <label className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 mb-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={notifySeller}
                onChange={(e) => setNotifySeller(e.target.checked)}
                className="accent-slate-800"
              />
              <span>Email <strong>{openHouse.sellerName || "the seller"}</strong></span>
            </label>
            <label className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={notifyAgent}
                onChange={(e) => setNotifyAgent(e.target.checked)}
                className="accent-slate-800"
              />
              <span>Email hosting agent <strong>{openHouse.hostingAgentName || "—"}</strong></span>
            </label>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md"
          >
            Keep Open House
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-700 hover:bg-red-800 rounded-md disabled:opacity-50"
          >
            {saving ? "Cancelling…" : "Cancel & Send Emails"}
          </button>
        </div>
      </div>
    </div>
  );
}
